// ToolPanel — pen kind / width / 旧版 GIF アイコンボタン群。MASK は分離済。
//
// Step 4 (frontend-developer, 2026-05-09 完了): Classic Layout のペン UI を
// 5 ボタン (旧 `[普通][水彩][マスク][逆マスク][消ゴム]`) → 4 ボタン
// (`[ブラシ][マスク][逆マスク][消ゴム]`) に統合 (consult 2026-05-09 / architecture.md
// §19.3 / session-2026-05-08.md §0.5).
// - ToolPanel 内のペン種 fieldset は `[ブラシ]` 1 ボタンに縮約 (= マスク 2 ボタンは
//   `MaskPanel.tsx` 側、消ゴムは機能ボタン側 toggle に分離). `[ブラシ]` 押下で
//   `pen='brush'` をセット、alpha 値は触らない (= palette A 値追従, 旧 `[普通]` の
//   「alpha=255 リセット」相当の挙動は廃止).
// - 旧 5 ボタン時代の helper (`penKindUi.ts`) は撤去. caller (ClassicLayout) が
//   直接 `pen='brush' | 'flat' | 'eraser'` をセットする形に整理.
// - protocol / `PenConfig.pen` の型 (`'brush' | 'flat' | 'eraser'`) / drawing engine
//   / DrawingCanvas 送信ロジックは不変 (= Step 2 backend で確定済).
// - 旧 Java 版の `[普通]`/`[水彩]` 2 ボタン懐古性は失われるが、CSP / Photoshop 流の
//   単一ブラシ設計に統合 (consult 2026-05-09).
//
// 2026-05-09 [フラット] ボタン追加 (consult 2026-05-09): ペン種 fieldset を
// `[ブラシ]` 1 ボタン → `[ブラシ][フラット]` 2 ボタン (排他選択) に拡張.
// `[フラット]` は `pen='flat'` (= 旧 alpha pen の max 蓄積モード) の UI 入口.
// 旧 Step 4 で `[マスク]`/`[逆マスク]` 経由でしか到達できなかった `'flat'` を
// `maskType` と完全に直交させ、3 pen × 3 maskType = 9 通りの組み合わせを UI 上
// で表現可能にする. `'brush'` の累乗カーブ build-up と `'flat'` の uniform alpha
// 塗り (= 半透明レイヤー塗り) を使い分け可能 (architecture.md §19.3 / §19.4 /
// §19.15 で `maskType` field は `pen` enum と独立した直交軸と明記済).
// callback API は `onSelectBrush()` → `onSelectPen(kind: 'brush' | 'flat')` に
// 拡張 (= 旧 1 callback では 2 ボタンを区別できないため). `[フラット]` ボタンは
// 旧版に対応する .gif アイコンが存在しないため text label のみ.
//
// 2026-05-09 [マーカー] ボタン追加 (consult 2026-05-09 試験追加): ペン種 fieldset を
// `[ブラシ][フラット][マーカー]` 3 ボタン (排他選択) に拡張. `[マーカー]` は
// `pen='marker'` (= `'flat'` の max 蓄積モードを維持しつつ濃さ筆圧連動を default ON
// にした第 3 のペン) の UI 入口. 直近実機検証で「`'flat'` + 濃さ筆圧連動」が
// コピックマーカー風として綺麗に出たため別ペン種化 (= `'flat'` 本来の「stroke 内
// uniform = 完全均一塗り」を壊さない). callback API は `onSelectPen(kind: 'brush'
// | 'flat' | 'marker')` に拡張. `[マーカー]` ボタンも旧版 .gif アイコンなしの
// text label のみ. α / β どのリリースに乗せるかは試験実装の実機検証後判断.
//
// Phase 6.7 R15 (consult 2026-05-05) / Step 4 (consult 2026-05-09) ：
// - ペン種ボタンと機能ボタンの見た目を `legacyToggleButtonClass` で完全統一
//   (idle=白 / hover=オレンジ薄 / selected=オレンジ薄 + insetFrame).
// - 消ゴムは機能ボタン側 toggle (`onToggleEraser` 専用 callback). pen='eraser' の
//   とき `[ブラシ]` ボタンは non-selected (= 機能ボタン「消ゴム」が pressed 状態).
//
// Phase 6.7-B-fix Item 3 / Item 4 / Item 5:
// - ペン幅 6 段階を旧版風に縦並びで配置.
// - MASK 関連 UI は `MaskPanel.tsx` に分離 (props も不要に)。
// - 選択中 cell に **左上 + 右下にオレンジ三角 + 黒枠** (`legacyFrames.tsx:SelectedMarker`)
//   を適用.
// - `outsetFrame` / `insetFrame` は `legacyFrames.tsx` から import。
//
// Phase 6.7 R11 (consult 2026-05-05):
// - ペン幅 cell 配置を **横 2 x 縦 3** (列優先 `index = ix*3 + iy`, ix=0,1 / iy=0,1,2)
//   に変更. 旧版 `PaintToolCanvas.drawPenWidthCursor` (line 674-702) 踏襲.
//   - 並び順: 左列 (上→中→下) = index 0/1/2 / 右列 (上→中→下) = index 3/4/5.
// - 6 cell の値は `penWidths` props (mutable state, ClassicLayout 側で保持).
//   太さ調整バーで「選択中 cell の値」をユーザが上書きできる. リロードで
//   `[1,2,3,4,8,16]` (`DEFAULT_PEN_WIDTHS`) に戻る (in-memory のみ, MVP 範囲).
// - 6 cell の下に `<PenWidthSlider />` (太さ調整バー). R10 と同じ Canvas slider
//   修正パターンを最初から適用済.
//
// Phase 6.7 R11-fix (2026-05-05): 「スライダーで cell 選択が移動するバグ」修正.
// - 旧実装は `pen.width === penWidths[i]` で **逆引きで** selected を判定して
//   いたため, スライダー drag で cell 値を上書きすると `pen.width` が他 cell と
//   一致して選択が飛んだ.
// - 修正: `selectedPenWidthIndex` props で **caller (ClassicLayout) から
//   独立した state として** 受け取る. cell click は `onPenWidthCellClick(index)`
//   経由で caller に通知, slider drag は `onPenWidthValueChange(selectedIdx, w)`
//   で「選択中 cell の値だけ」を更新する (selectedIdx は触らない). 旧版
//   `PaintToolCanvas.java:91` の `selPenWidth` / `penWidths` 完全分離設計を踏襲.
//
// Phase 6.7-B-fix-2B-b: ペン幅 cell の静的 1px 黒 border (`border border-black`) を
// 復活 (色被り判別 + 隣接 cell 境界の識別のため). focus ring は旧 Java applet UI
// 完全再現要求により抑制 (`focus:outline-none focus-visible:outline-none`)。
//
// (architecture.md §16 / §19.3 / §19.4)

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PenConfig } from '../../../domain/PenConfig.js';
import { createClassicToolsMovedSeenStore } from '../../../infrastructure/classicToolsMovedSeen.js';
import { useGuardedTap } from '../../hooks/useGuardedTap.js';
import { useTapActivate } from '../../hooks/useTapActivate.js';
import { useConnectionLocked } from '../../providers/ConnectionLockContext.js';
import { legacyToggleButtonClass, outsetFrame, SelectedMarker } from './legacyFrames.js';
import { PenWidthSlider } from './PenWidthSlider.js';
import { getPenWidthCellDotDisplay, PEN_WIDTH_CELL_ORDER } from './penWidthSlider.js';
import { placeToolsFlyoutTop } from './toolsFlyoutPlacement.js';

/**
 * 6 プリセットの初期値 (互換 export). 新規コードは
 * `penWidthSlider.ts:DEFAULT_PEN_WIDTHS` を直接 import すること.
 *
 * 名前 `LEGACY_PEN_WIDTHS` は Phase 6.7-B Task R5 当時 (旧 Java 値そのもの) の
 * 名残で, **現在は新版 1500² キャンバス向けに振り直した値** を保持する
 * (architecture.md §16 / consult 2026-05-09):
 *
 *   旧版 `[1, 2, 3, 4, 8, 16]` → 新版 `[1, 3, 5, 10, 20, 40]`
 *
 * 旧版値は `PaintToolCanvas.java:86` の `drawWidths` を参照. 新版は 1500² キャン
 * バス (旧 800×450 の 6.25× 面積) で運用に乗る間隔に振り直し済 (= 旧値だと
 * 細すぎる、`MAX_PEN_WIDTH = 60` も同根拠).
 *
 * Phase 6.7 R11 で「ユーザが太さ調整バーで cell 値を上書き可能」になり, この
 * 配列は **初期値** としての役割しか持たない. ClassicLayout 側で
 * `useState<number[]>([...DEFAULT_PEN_WIDTHS])` の seed として使われる.
 */
export const LEGACY_PEN_WIDTHS: ReadonlyArray<number> = [1, 3, 5, 10, 20, 40];

export interface ToolPanelProps {
  readonly pen: PenConfig;
  /**
   * Phase 6.7 R15: 「消ゴム」機能ボタンの toggle. ON で `pen='eraser'` に切替え、
   * 同時に caller 側で直前のペン種を保存. 再度押下されたら直前のペン種に戻す.
   */
  readonly onToggleEraser: () => void;
  /**
   * R11-fix: 「現在の幅」を pen.width に直接 set する低レベル callback. cell click
   * では使わず (= `onPenWidthCellClick` が selectedIndex 経由で更新する),
   * スライダー drag 時の「pen.width を新しい値に追従」専用に残す.
   */
  readonly onWidthChange: (width: number) => void;
  /**
   * R11: 6 cell の現在値 (mutable). ClassicLayout が state で保持し, 太さ調整バー
   * のドラッグで「選択中 cell の値」を更新する. 初期値は `DEFAULT_PEN_WIDTHS`.
   */
  readonly penWidths: ReadonlyArray<number>;
  /**
   * R11-fix: 選択中 cell の index (0..5). `penWidths` とは独立して caller が保持
   * する. これがあるおかげで `pen.width === penWidths[i]` の逆引きが不要になり,
   * スライダー drag 中に cell 値が他 cell と一致しても選択中 cell が動かない.
   * (旧版 `PaintToolCanvas.selPenWidth` 完全踏襲).
   */
  readonly selectedPenWidthIndex: number;
  /**
   * R11-fix: cell click 時に caller に通知. caller は `selectedPenWidthIndex` を
   * 更新し, あわせて `pen.width = penWidths[index]` も追従させる.
   */
  readonly onPenWidthCellClick: (index: number) => void;
  /**
   * R11: 太さ調整バーで選択中 cell の値を上書きする callback. caller は
   * `penWidths[selectedIdx] = newWidth` + `pen.width = newWidth` の両方を更新する.
   * R11-fix: selectedIdx は変更しない (スライダー drag では cell 選択は移動しない).
   */
  readonly onPenWidthValueChange: (selectedIndex: number, newWidth: number) => void;
  /**
   * Phase 7 Task 7-8b: 「画像貼付」アイコンの click → 隠れた `<input type="file">` を
   * トリガし, 選択された File を渡す. `undefined` (or `null`) のときボタン自体を
   * 隠す (= `Perm.IMAGEPASTE` 未保有 / 無権限ユーザ).
   *
   * Async: caller (`useImagepaste.start`) は createImageBitmap → engine
   * preview の chain を await するので、`<input>` 側の reset は完了後に行う.
   */
  readonly onImagepasteFile?: (file: File) => void | Promise<void>;
  /**
   * 範囲の道具 (範囲の移動・コピー、docs/_wip/region-move-2026-10-15.md「入口」): 縦を増やさないため、
   * 「画像貼付」ボタンを「道具 ▸」に置き換え、その小窓に `[範囲] [画像貼付]` を並べる。
   * `regionActive` は範囲の道具が選択中か (「道具 ▸」と小窓の [範囲] を点灯)。
   * 省略時は小窓に [範囲] を出さない。
   */
  readonly regionActive?: boolean;
  readonly onSelectRegion?: () => void;
  /** 「道具 ▸」と小窓の [範囲] に「New!」バッジを出すか。範囲の道具を一度でも選ぶと消える。 */
  readonly showRegionNewBadge?: boolean;
  /** 「道具 ▸」の印 / 初回お知らせの seen 永続化の差し替え口 (テスト用)。 */
  readonly toolsMovedSeenStore?: ReturnType<typeof createClassicToolsMovedSeenStore>;
  /**
   * Phase 6.8-A Task 1: 全消し (bomb) ボタン enable 判定. `canClearWhiteboard` で
   * `Perm.CLEAR` 保有 OR `room.clearOpen === true` を算出した結果を受け取る.
   * `false` のときボタンは disabled + tooltip「全消しの権限がありません」表示.
   */
  readonly canClear: boolean;
  /**
   * Phase 6.8-A Task 1: 全消し実行 callback. ボタン click → confirm dialog OK で呼ぶ.
   * 内部で `whiteboard.clear` を送る (= `useSession().sendClear`).
   * server 拒否は `system.error` 経由で chat system 行に出るので追加 UI 反映不要.
   */
  readonly onClear: () => void;
  /**
   * Phase 6.8-A Task 2: キャプ (camera) ボタン click. caller (ClassicLayout) が
   * canvasRef 経由で PNG dataURL を生成し programmatic `<a download>` を click
   * する handler を組み立てる. ToolPanel 内に Canvas 参照を持ち込まない設計
   * (= 関心の分離). 権限不要 (= 全ユーザが使える).
   */
  readonly onCapture: () => void;
  /**
   * Phase 6.9: 「名表示 (name)」ボタン toggle. caller (ClassicLayout) が
   * drawing engine の `setNameLabelMode` を経由して反映する. 同時に caller 側で
   * `useSyncExternalStore` 経由で `nameLabelMode` を購読し pressed 状態を反映.
   */
  readonly nameLabelMode: boolean;
  readonly onToggleNameLabelMode: () => void;
  /**
   * Phase 6.9: 「拡大鏡」ボタン (`loupe_plus` / `loupe_minus`) を別ボタンに分割.
   * `zoom` は drawing engine の現在 zoom (0.1..16、CSP 方式の刻みで 1x 未満にも縮小できる
   * = 2026-09-29). `zoomMin` / `zoomMax` は `ZOOM_FIT_MIN` / `ZOOM_MAX` を caller 側から渡す (ToolPanel から engine module
   * を直接 import しない設計, テスト容易性のため). ボタン disabled 判定は
   * `zoom === zoomMin` (`-`) / `zoom === zoomMax` (`+`).
   */
  readonly zoom: number;
  readonly zoomMin: number;
  readonly zoomMax: number;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  /**
   * 2026-05-09 ペン専用モード (Palm Rejection、consult 2026-05-09).
   *
   * `true` のとき DrawingCanvas は `pointerType !== 'pen'` (= mouse / touch) を
   * 無視する (= 指誤爆防止). チェックボックス UI は PenPressureConfig 直前
   * (= ペン詳細セクションの末尾) に配置する.
   *
   * 自動 ON 復帰: `pointerType === 'pen'` を検知すると DrawingCanvas が caller
   * (= ClassicLayout) の `onPenAutoEnable` を呼んで毎回 ON に戻す. このため
   * 「ユーザがマウスで一時 OFF」 → 「ペンを使うと自動復帰」 が継続的に動く.
   *
   * α 期は session-local (= localStorage 不使用、ページ再読み込みでリセット).
   */
  readonly penOnlyMode: boolean;
  readonly onPenOnlyModeChange: (next: boolean) => void;
  /**
   * 2026-05-17 iPad scroll 救済 (consult 2026-05-17): 手のひらモード持続トグル.
   *
   * `true` のとき ClassicLayout 側の `isPanning` 派生値が常時 ON になり、canvas
   * wrapper への pointer event は pan drag (= overflow-auto を programmatic
   * scroll) として消費される. 描画は不可 (DrawingCanvas に pointerEvents:none).
   *
   * iPad / 外付けキーボード無し環境の救済用: `touch-action: none` で 2 本指
   * scroll が封じられている (= Apple Pencil 水平 sweep を iPadOS Selection と
   * 誤判定する挙動の対策で外せない) ため、Space hold 経路の代替として持続的な
   * pan モードトグルを別途用意する.
   *
   * Space hold とは独立した state ソース: Space を押しても toggle OFF には
   * ならず、toggle ON のまま Space を押しても OFF にならない (= Space は
   * 一時的 boost として動作). caller (ClassicLayout) 側で OR を取って
   * effective な `isPanning` を計算する.
   */
  readonly panToggleOn: boolean;
  readonly onPanToggleChange: (next: boolean) => void;
  /**
   * 2026-05-17 旧版 PreviewFrame 復活 (旧 PreviewCanvas.java / PreviewFrame.java
   * 踏襲): キャンバス全体プレビューの開閉トグル. ON でサムネイル floating
   * パネル (`PreviewOverlay`) を表示、画面右下に出てクリック/ドラッグで
   * スクロール位置を変更できる. 拡大時に「今どこを描いてるか」を見失わない
   * ための旧版機能.
   */
  readonly previewOpen: boolean;
  readonly onTogglePreview: () => void;
  /**
   * 2026-05-31 layer-popup-visibility-toggle §2.2.2 (`w3-classic-layers.md §2.2.2`):
   * LayerPopup の visibility toggle. §2.2.1 の最初の実装 (commit 34262ad) では
   * `ArgbSliderPanel` 内に統合していたが、post-implementation 調整で **Preview
   * toggle と同じ「常時参照型 floating panel の visibility 切替」性質** を持つ
   * ため、ToolPanel に並べて UX 集約性を高める. 表示は Preview button の直下.
   * styling は Preview button と完全同型 (`legacyToggleButtonClass(layerPopupVisible)`).
   */
  readonly layerPopupVisible: boolean;
  readonly onToggleLayerPopup: () => void;
  /**
   * 2026-06-07 キャンバス左右反転 (view flip、`docs/_wip/canvas-flip.md` §4).
   * 旧 Java 版踏襲のデッサン確認用「反転」トグル. engine が `flipX` 状態を所有し
   * ClassicLayout がミラーした値を渡す (= Preview / レイヤー toggle と同型の
   * 「表示状態の on/off トグル」). 反転中も描ける (座標は真の canvas 座標で記録).
   */
  readonly flipX: boolean;
  readonly onToggleFlip: () => void;
  /**
   * 2026-06-20 ペン種選択をフロート「ペン」に集約 (consult 2026-06-20、A 採択)。
   * これまで ToolPanel に並んでいたインライン ペン種 fieldset ([ブラシ][サインペン]) +
   * [テクスチャ] 入口 + TexturePopup を、常設フロート「ペン」(`PenPopup`) 1 個に集約。
   * ToolPanel には [プレビュ]/[レイヤー] と同型の **[ペン] トグル入口 1 個** だけを置き、
   * 押すとフロートを開閉する (= ペン種ボタンを増やさず縦圧を解消、§7 / §1.1)。
   *
   * - `penPopupOpen`: フロート「ペン」の開閉状態 (ClassicLayout が visible state を持ち、
   *   PreviewOverlay / LayerPopup と同型にミラー + localStorage 永続化)。ボタンの
   *   aria-pressed に流れる。
   * - `onTogglePenPopup`: 入口ボタン押下でフロートを開閉。
   *
   * 入口ラベルは固定「ペン」(consult 2026-06-20: 現ペン種併記はラベルが伸びてサイドバー
   * 幅が広がる/「ペン:」不要との指摘で撤去)。aria-pressed はフロート開閉状態を反映。
   */
  readonly penPopupOpen: boolean;
  readonly onTogglePenPopup: () => void;
}

export const ToolPanel = ({
  pen,
  onToggleEraser,
  onWidthChange,
  penWidths,
  selectedPenWidthIndex,
  onPenWidthCellClick,
  onPenWidthValueChange,
  onImagepasteFile,
  regionActive = false,
  onSelectRegion,
  showRegionNewBadge = false,
  toolsMovedSeenStore,
  canClear,
  onClear,
  onCapture,
  nameLabelMode,
  onToggleNameLabelMode,
  zoom,
  zoomMin,
  zoomMax,
  onZoomIn,
  onZoomOut,
  penOnlyMode,
  onPenOnlyModeChange,
  panToggleOn,
  onPanToggleChange,
  previewOpen,
  onTogglePreview,
  layerPopupVisible,
  onToggleLayerPopup,
  flipX,
  onToggleFlip,
  penPopupOpen,
  onTogglePenPopup,
}: ToolPanelProps): React.ReactNode => {
  // 再接続中は全消し等が止まる: tooltip を「権限なし」ではなく接続待ちの文言にする。
  const connectionLocked = useConnectionLocked();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onImagepasteClick = (): void => {
    fileInputRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // 同じファイルを 2 回連続で選んでも change が発火するよう reset する.
    e.target.value = '';
    if (file === undefined || onImagepasteFile === undefined) return;
    await onImagepasteFile(file);
  };

  // R11-fix: 選択中 cell index は caller から直接渡される独立 state.
  // 0..penWidths.length-1 の範囲外なら defensive に 0 (左列上) に倒す
  // (本来 caller 側で範囲を保証している).
  const selectedIndex =
    selectedPenWidthIndex >= 0 && selectedPenWidthIndex < penWidths.length
      ? selectedPenWidthIndex
      : 0;
  const sliderValue = penWidths[selectedIndex] ?? pen.width;

  const onSliderChange = (next: number): void => {
    // R11-fix: スライダー drag は「選択中 cell の値」だけを上書き.
    // selectedIndex 自体は触らない (旧版 mouseDragged 踏襲).
    // 1. cell 値を上書き
    onPenWidthValueChange(selectedIndex, next);
    // 2. pen.width も同じ値に追従 (cell の dot サイズが追従し, 描画も次ストロークから反映)
    onWidthChange(next);
  };

  // 文字ラベルトグル / 入口ボタンの起動を pointer ベースへ (ペン / Windows Ink の click
  // 不発対策、useTapActivate)。LegacyIconButton / ZoomHalf と同じ堅牢化を、ここの plain
  // button 群 (ペン入口 / 手のひら / プレビュ / レイヤー / 反転 / 画像) にも揃える。onClick
  // (キーボード用) は tap 側が温存し、pointer 起動直後の二重 click はデデュープする。
  // ペン幅 cell は .map 内なので hook を呼べず、専用 `PenWidthCell` 子側で tap 化している。
  const penPopupTap = useTapActivate(onTogglePenPopup);
  const panTap = useTapActivate(() => onPanToggleChange(!panToggleOn));
  const previewTap = useTapActivate(onTogglePreview);
  const layerTap = useTapActivate(onToggleLayerPopup);
  const flipTap = useTapActivate(onToggleFlip);
  const imagepasteTap = useTapActivate(connectionLocked ? undefined : onImagepasteClick);

  // 「道具 ▸」の小窓 (画像貼付があった場所。縦を増やさない)。開くと [範囲] [画像貼付]。
  // 移した直後は「道具 ▸」に小さな印を付け、初めて開いたときだけ「画像貼付はここに移りました」。
  const seenStoreRef = useRef(toolsMovedSeenStore ?? createClassicToolsMovedSeenStore());
  const [toolsOpen, setToolsOpen] = useState(false);
  const [movedSeen, setMovedSeen] = useState<boolean>(() => seenStoreRef.current.hasSeen());
  const [movedNotice, setMovedNotice] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState<{
    left: number;
    top: number;
    anchorTop: number;
  } | null>(null);
  const toolsButtonRef = useRef<HTMLButtonElement | null>(null);
  const flyoutRef = useRef<HTMLFieldSetElement | null>(null);
  const hasToolsEntry = onImagepasteFile !== undefined || onSelectRegion !== undefined;
  const closeTools = (): void => setToolsOpen(false);
  const toggleTools = (): void => {
    if (toolsOpen) {
      setToolsOpen(false);
      return;
    }
    const r = toolsButtonRef.current?.getBoundingClientRect();
    if (r !== undefined) {
      // トリガに重ならない側へ出す (画面の左半分なら右、右半分なら左)。右ドック時は左へ。
      const toRight = r.left + r.width / 2 < window.innerWidth / 2;
      setFlyoutPos({
        left: toRight ? r.right + 4 : Math.max(4, r.left - 4 - 132),
        top: Math.max(4, Math.min(r.top, window.innerHeight - 96)),
        anchorTop: r.top,
      });
    }
    // 初めて開いた: お知らせを出し、印を消す (小窓を閉じるまでお知らせは残る)。
    if (!seenStoreRef.current.hasSeen()) {
      setMovedNotice(true);
      seenStoreRef.current.markSeen();
      setMovedSeen(true);
    }
    setToolsOpen(true);
  };
  // 開いた直後 (paint 前) に実測の高さで縦位置を直す: 画面の下端とチャット欄にかからない位置へ上に寄せる。
  useLayoutEffect(() => {
    if (!toolsOpen || flyoutPos === null) return;
    const el = flyoutRef.current;
    if (el === null) return;
    const fr = el.getBoundingClientRect();
    const chatEl = document.querySelector('section[aria-label="チャット"]');
    const cr = chatEl?.getBoundingClientRect();
    const top = placeToolsFlyoutTop({
      anchorTop: flyoutPos.anchorTop,
      height: fr.height,
      left: fr.left,
      right: fr.right,
      viewportHeight: window.innerHeight,
      chat: cr === undefined ? null : { top: cr.top, left: cr.left, right: cr.right },
    });
    if (Math.abs(top - flyoutPos.top) > 0.5) setFlyoutPos({ ...flyoutPos, top });
  }, [toolsOpen, flyoutPos]);
  const toolsTap = useGuardedTap(hasToolsEntry ? toggleTools : undefined);
  const regionTap = useGuardedTap(
    onSelectRegion === undefined
      ? undefined
      : () => {
          onSelectRegion();
          closeTools();
        },
  );
  useEffect(() => {
    if (!toolsOpen) {
      setMovedNotice(false);
      return;
    }
    const onDown = (e: PointerEvent): void => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (flyoutRef.current?.contains(t) || toolsButtonRef.current?.contains(t)) return;
      setToolsOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setToolsOpen(false);
        toolsButtonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  return (
    <section aria-label="ツール" className="flex flex-col gap-2">
      {/* 2026-06-20 ペン種選択をフロート「ペン」に集約 (consult 2026-06-20、A 採択):
          インライン ペン種 fieldset ([ブラシ][サインペン]) + [テクスチャ] 入口 +
          TexturePopup を、常設フロート「ペン」(`PenPopup`) 1 個に集約。ToolPanel は
          [プレビュ]/[レイヤー] と同型の **[ペン] トグル入口 1 個** だけを置き、押すと
          フロートを開閉する (= ペン種ボタンを増やさず縦圧を解消、§7 / §1.1)。

          §9.3 (consult 2026-05-09)「懐古性は配色・配置・操作感で維持、ペン種ボタンの
          "数"では維持しない」: Java 版に [ブラシ]/[サインペン] は無く新版の構築物なので、
          フロート集約は懐古純度を損なわない。常設フロートなので開きっぱなしにすれば
          ペン切替は 1 クリックのまま (ドックから浮くだけ)。

          入口ラベルは固定「ペン」(consult 2026-06-20: 現ペン種併記はラベルが伸びて
          サイドバー幅が広がる/「ペン:」が不要、との指摘で撤去)。aria-pressed はフロート
          開閉状態 (`penPopupOpen`) を反映 ([プレビュ]/[レイヤー] と同型)。 */}
      <fieldset aria-label="ペン種" className="flex flex-col gap-0.5">
        <button
          type="button"
          aria-label="ペン"
          aria-pressed={penPopupOpen}
          {...penPopupTap}
          title={penPopupOpen ? 'ペン種を閉じる' : 'ペン種 (ブラシ/サインペン/テクスチャ)'}
          className={`flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[10px] font-bold leading-tight ${legacyToggleButtonClass(penPopupOpen)}`}
        >
          ペン
        </button>
      </fieldset>

      {/* 2026-05-17 iPad scroll 救済 (consult 2026-05-17): 持続的「手のひら」モード.
          ペン種 fieldset と並列に置くのは「描画モード ↔ pan モード」のモード切替に
          見せたいから (= サイドバー下端の小 checkbox では iPad ユーザに tap しにくく、
          概念的にもモードスイッチであることが伝わらなかった).
          - 消ゴム (機能ボタン側 toggle) は **描画モード内サブ切替** (= 黒で削る).
          - 手のひら はそれとは別軸で **描画モードそのものを離脱** して pan に切替.
          - Space hold (`spaceHeld`) は CSP/PS 流儀の一時 boost. これは持続トグル.
            両者は OR で `isPanning` を駆動 (= ClassicLayout 側 派生値).
          - ON 中は canvas wrapper に `pointerEvents: 'none'` が掛かり描画が遮断
            される副作用がある (= 既存仕様、wrapper の overflow-auto を 1 本指
            ドラッグでスクロールさせるための代償). */}
      {/* 2026-05-27 省スペース化 (classic-sidebar-compaction.md §2.1): 「手のひら」の
          視覚 legend を削除. 直下のボタンと完全に同じ文字列で純粋な冗長だったため.
          fieldset の `aria-label="手のひら"` は残して a11y のグルーピングを維持. */}
      <fieldset aria-label="手のひら" className="flex flex-col gap-0.5">
        <button
          type="button"
          aria-pressed={panToggleOn}
          {...panTap}
          data-manual="view.pan"
          title="ON で canvas をドラッグスクロール (iPad / キーボード無しでも pan 可能)"
          className={`relative flex h-6 items-center justify-center whitespace-nowrap text-[10px] font-bold ${legacyToggleButtonClass(panToggleOn)}`}
        >
          手のひら
          {panToggleOn ? <SelectedMarker /> : null}
        </button>
      </fieldset>

      {/* ペン幅 6 段階. R11 で 横 2 x 縦 3 に変更 (列優先 index 配置: 左列 0/1/2,
          右列 3/4/5). `grid-flow-col grid-rows-3` で 配列順を 縦に流して 2 列に折る.
          各 cell に幅に比例する黒丸を描いて視覚化, 選択中は SelectedMarker (黄色三角). */}
      <fieldset aria-label="ペン幅" className="flex flex-col gap-0.5">
        {/* 表示専用ラベル: iPad Apple Pencil ジェスチャ抑止 (consult 2026-05-09、
            「ペン種」legend と同根拠). */}
        <legend className="select-none px-1 text-[10px] font-bold text-black/70">太さ</legend>
        <div className="grid grid-flow-col grid-cols-2 grid-rows-3 gap-0.5">
          {PEN_WIDTH_CELL_ORDER.map((idx) => (
            <PenWidthCell
              // PEN_WIDTH_CELL_ORDER の値は固定 (`[0,1,2,3,4,5]`) で配列順 (cell 位置)
              // と一致するので, 値そのものを key にすれば「インデックスを key にして
              // いる (= 順序が変わると state が壊れる)」問題は発生しない (cell 位置は
              // ユーザカスタマイズで変動しない).
              key={idx}
              width={penWidths[idx] ?? 0}
              selected={idx === selectedIndex}
              // R11-fix: cell 起動は selectedPenWidthIndex を更新する高レベル callback
              // 経由. caller (ClassicLayout) が selectedIndex 更新 + pen.width 追従の
              // 両方を担当する.
              onActivate={() => onPenWidthCellClick(idx)}
            />
          ))}
        </div>
        {/* 太さ調整バー. 選択中 cell の値をドラッグで上書き. R10 と同じ Canvas
            slider 修正パターンを内部で適用済 (画面外ドラッグでも 0/16 に届く). */}
        <div className="mt-1 flex justify-center">
          <PenWidthSlider value={sliderValue} onChange={onSliderChange} />
        </div>
      </fieldset>

      {/* GIF 機能アイコン群. 縦 1 列に並ぶ (旧版 room.png 踏襲). */}
      {/* 2026-06-13 省スペース化続編 (classic-sidebar-compaction.md §7): 機能ボタンの
          h-7 (28px) → h-[26px] + 本 fieldset の gap-1 → gap-0.5 で、道具系入口ボタン
          (図形/追加ペンのフロート展開) 用の縦スペース ≒ 26px を捻出. ペン種 / 手のひら /
          ペン幅セル (h-6) はタップ最重要部品のため据え置き. */}
      <fieldset aria-label="ツールアイコン" className="flex flex-col gap-0.5">
        {/* Phase 6.7 R15: 消ゴムは toggle (押下のたびに ON ↔ OFF). ClassicLayout
            側で `lastNonEraserPen` を保持し, OFF 時に直前のペン種に復帰する. */}
        <LegacyIconButton
          label="消ゴム"
          icon="keshi"
          onClick={onToggleEraser}
          pressed={pen.pen === 'eraser'}
          manualId="draw.eraser"
        />
        {/* Phase 6.9: 「名表示 (name)」 toggle. ON で drawing engine 上の
            NameLabel mode を有効化し, ストローク始点の displayName を DOM
            overlay (NameLabelOverlay) で表示する. pressed/idle の見た目は
            機能ボタン (LegacyIconButton) 共通. */}
        <LegacyIconButton
          label="名表示"
          icon="name"
          onClick={onToggleNameLabelMode}
          pressed={nameLabelMode}
          tooltip={nameLabelMode ? '名表示中' : '名表示を有効にする'}
          manualId="together.name-label"
        />
        {/* Phase 6.8-A Task 1: 全消し (bomb) を実体化. `Perm.CLEAR` 保有 OR
            `room.clearOpen` のとき enable, 押下で確認モーダル (ConfirmDialog,
            destructive) → OK で `whiteboard.clear` 送信 (caller の onClear
            callback). 権限が無いときは disabled + tooltip「全消しの権限が
            ありません」.
            2026-06-07 α-feedback O: 「全消しが消ゴムの真下にあって怖い」報告を
            受け, 頻用の消ゴムから 1 つ離す目的で 名表示 の下へ移動 (消ゴム →
            名表示 → 全消し の順). 確認モーダルは既存のため追加せず配置のみで
            誤操作を緩和. */}
        <LegacyIconButton
          label="全消し"
          icon="bomb"
          disabled={!canClear}
          onClick={canClear ? onClear : undefined}
          tooltip={
            connectionLocked
              ? 'つながったら使えます'
              : canClear
                ? 'キャンバスを全消し'
                : '全消しの権限がありません'
          }
          manualId="draw.clear"
        />
        {/* Phase 6.8-A Task 2: キャプ (camera) を実体化. caller の
            `onCapture` は canvasRef → `toDataURL('image/png')` → `<a download>`
            の chain で PNG をダウンロードさせる. 権限不要. */}
        <LegacyIconButton
          label="キャプ"
          icon="camera"
          onClick={onCapture}
          tooltip="キャンバスを PNG で保存"
          manualId="keep.capture"
        />
        {/* kick ボタンは MemberPanel に配置済 (Phase 7 Task 7-9) のため、
            ToolPanel からは削除. 旧版 PaintToolCanvas には kick ボタンが
            あったが新版では参加者リスト経由 1 経路に統一 (consult 2026-05-06,
            6.8-B 確定方針). GIF (kick_a1/a2.gif) は将来再利用に備えて残置. */}
        {/* 2026-05-16 fb: 旧版 PaintToolCanvas.java:554-585 踏襲で 1 つの枠を
            横半分ずつ + / - に割る形に変更. loupe GIF が元から 20x20px (= 半幅
            サイズ) なので並べるだけで旧版と同じ見た目になる. drawing engine が
            1..16 で clamp するので caller は `zoom + 1` / `zoom - 1` を素直に
            渡せばよい. 端到達時のみ片側 disabled. */}
        <ZoomSplitButton
          zoom={zoom}
          zoomMin={zoomMin}
          zoomMax={zoomMax}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
        {/* 2026-05-17 旧版 PreviewFrame 復活: キャンバス全体プレビュー toggle.
            ON で画面右下に floating panel が出てサムネ + 表示範囲枠 + クリック/
            ドラッグでスクロール位置変更. .gif アイコンが旧版に無いため text label.
            ペン種ボタンと共通の `legacyToggleButtonClass` で見た目を統一. */}
        <button
          type="button"
          aria-label="プレビュー"
          aria-pressed={previewOpen}
          {...previewTap}
          data-manual="view.preview"
          title={previewOpen ? 'プレビュー表示中' : 'キャンバス全体プレビューを表示'}
          className={`flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[10px] font-bold ${legacyToggleButtonClass(previewOpen)}`}
        >
          プレビュ
        </button>
        {/* 2026-05-31 layer-popup-visibility-toggle §2.2.2 (`w3-classic-layers.md §2.2.2`):
            LayerPopup visibility toggle. Preview toggle と並んで「常時参照型 floating
            panel の visibility 切替」を担う. 元々 §2.2.1 で `ArgbSliderPanel` 内に
            統合していたが post-implementation 調整で UX 集約性を優先して ToolPanel
            の Preview button 直下へ移動. styling は Preview button と完全同型. */}
        <button
          type="button"
          aria-label="レイヤー"
          aria-pressed={layerPopupVisible}
          {...layerTap}
          data-manual="layer.overview"
          title={layerPopupVisible ? 'レイヤーパネル表示中' : 'レイヤーパネルを表示'}
          className={`flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[10px] font-bold ${legacyToggleButtonClass(layerPopupVisible)}`}
        >
          レイヤー
        </button>
        {/* 2026-06-07 キャンバス左右反転 (view flip、`canvas-flip.md` §4): 旧 Java 版
            踏襲のデッサン確認用「反転」トグル. Preview / レイヤー toggle と同じ
            `legacyToggleButtonClass` パターン. ON で canvas が CSS transform で
            鏡像表示になる (engine 側で適用). 反転中も描ける (= 座標は真の canvas
            座標で記録され反転は描画データに混入しない). .gif アイコン未整備のため
            text label. */}
        <button
          type="button"
          aria-label="反転"
          aria-pressed={flipX}
          {...flipTap}
          data-manual="view.flip"
          title={flipX ? '左右反転表示中 (V)' : 'キャンバスを左右反転 (V)'}
          className={`flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[10px] font-bold ${legacyToggleButtonClass(flipX)}`}
        >
          反転
        </button>
        {/* 「道具 ▸」: 旧「画像貼付」ボタンの場所 (2026-10-15 作者案、縦を増やさない)。押すと小窓に
            [範囲] [画像貼付]。画像貼付は `Perm.IMAGEPASTE` 保有時のみ (ClassicLayout が
            `onImagepasteFile` を渡す。未保有なら小窓に出さない — server も二重防御で reject するが、
            UI 側で先回りして 30 秒 rate-limit を消費させない)。将来の図形もここへ。 */}
        {hasToolsEntry && (
          <>
            <button
              ref={toolsButtonRef}
              type="button"
              aria-label={showRegionNewBadge ? '道具 (新機能)' : '道具'}
              aria-haspopup="true"
              aria-expanded={toolsOpen}
              title="範囲の移動・コピー / 画像貼付"
              data-testid="classic-tools-button"
              {...toolsTap}
              className={`relative flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[10px] font-bold ${legacyToggleButtonClass(toolsOpen || regionActive)}`}
            >
              道具 ▸
              {/* 範囲の New! バッジ (カーソル / 回転と同じ見た目)。下の橙の点 (移動の印) は
                  右上が重なるので、New! の間は点を出さない。 */}
              {showRegionNewBadge && onSelectRegion !== undefined ? (
                <span
                  aria-hidden="true"
                  data-testid="region-new-badge"
                  className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
                >
                  New!
                </span>
              ) : null}
              {/* 移した直後の印 (小窓を一度開くと消える)。意味は title / お知らせが担う装飾。 */}
              {!movedSeen && !(showRegionNewBadge && onSelectRegion !== undefined) && (
                <span
                  aria-hidden="true"
                  data-testid="classic-tools-badge"
                  className="absolute -top-1 -right-1 h-2 w-2 rounded-full border border-black bg-orange-400"
                />
              )}
            </button>
            {onImagepasteFile !== undefined && (
              /* Hidden file input — `display: none` ではなく opacity:0 + size
                 ゼロにすると Safari iOS 16 で click が発火しないことがある
                 ので、`hidden` 属性で十分 (DOM tree から外れないので
                 programmatic click が動く). accept は protocol schema が
                 受ける 3 種に絞る. */
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif"
                hidden
                aria-hidden="true"
                tabIndex={-1}
                onChange={(e) => {
                  void onFileChange(e);
                }}
              />
            )}
            {toolsOpen && flyoutPos !== null
              ? createPortal(
                  <fieldset
                    ref={flyoutRef}
                    aria-label="道具"
                    data-testid="classic-tools-flyout"
                    style={{
                      position: 'fixed',
                      left: flyoutPos.left,
                      top: flyoutPos.top,
                      zIndex: 45,
                    }}
                    className={`m-0 flex w-[132px] flex-col gap-1 bg-[var(--color-bg-soft)] p-1 text-black ${outsetFrame}`}
                  >
                    {movedNotice && onImagepasteFile !== undefined && (
                      <p
                        data-testid="classic-tools-moved-notice"
                        className="m-0 text-[10px] font-bold leading-tight text-[var(--color-text)]"
                      >
                        画像貼付はここに移りました
                      </p>
                    )}
                    {onSelectRegion !== undefined && (
                      <button
                        type="button"
                        aria-pressed={regionActive}
                        aria-label={showRegionNewBadge ? '範囲 (新機能)' : '範囲'}
                        title="範囲を選んで移動・コピー"
                        data-manual="draw.region-move"
                        data-testid="classic-tools-region"
                        {...regionTap}
                        className={`relative flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[11px] font-bold ${legacyToggleButtonClass(regionActive)}`}
                      >
                        範囲
                        {showRegionNewBadge ? (
                          <span
                            aria-hidden="true"
                            data-testid="region-new-badge"
                            className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
                          >
                            New!
                          </span>
                        ) : null}
                      </button>
                    )}
                    {onImagepasteFile !== undefined && (
                      <button
                        type="button"
                        aria-label="画像貼付"
                        title={connectionLocked ? 'つながったら使えます' : '画像を貼り付ける'}
                        // 再接続中は消さずに灰色で残す (並びがずれない)。
                        disabled={connectionLocked}
                        data-manual="draw.imagepaste"
                        data-testid="classic-tools-imagepaste"
                        {...imagepasteTap}
                        onPointerUp={(e) => {
                          imagepasteTap.onPointerUp(e);
                          // 起動したら小窓は閉じる (ファイル選択は pointerup 内の click() で開く)。
                          if (!connectionLocked && e.isPrimary && e.button === 0) closeTools();
                        }}
                        className={`flex h-[26px] items-center justify-center whitespace-nowrap p-0.5 text-[11px] font-bold ${legacyToggleButtonClass(false)} ${connectionLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        画像貼付
                      </button>
                    )}
                  </fieldset>,
                  document.body,
                )
              : null}
          </>
        )}
      </fieldset>

      {/* 2026-05-09 ペン専用モード (Palm Rejection、consult 2026-05-09).
          Apple Pencil / Wacom 液タブ等の `pointerType === 'pen'` 以外を canvas
          で無視する. CSP / Procreate / Photoshop に倣う「ペン専用」 label.
          ペン詳細セクション (= 直下に PenPressureConfig が並ぶ) の末尾に配置.
          自動 ON 復帰: ペンを使うたびに DrawingCanvas が onPenAutoEnable を
          呼んで毎回 ON に戻す (= マウスで一時 OFF にしても次のペン入力で復帰).

          表示専用ラベル領域に `select-none` を付ける流儀は他の fieldset と
          揃える (= iPad Apple Pencil の Text Highlighter ジェスチャ抑止). */}
      <label
        className="flex select-none items-center gap-1 whitespace-nowrap text-[10px] font-bold leading-none"
        title="チェック ON でペン (Apple Pencil / 液タブ等) 以外の入力を無視 (= 指誤爆防止)"
      >
        <input
          type="checkbox"
          checked={penOnlyMode}
          onChange={(e) => onPenOnlyModeChange(e.target.checked)}
          className="h-3 w-3 cursor-pointer"
          aria-label="ペン専用モード ON/OFF"
          data-manual="draw.pen-only"
        />
        <span>ペン専用</span>
      </label>
    </section>
  );
};

/**
 * ペン幅 6 段階の 1 cell (横 2 × 縦 3 グリッドの 1 つ). 幅に比例する黒丸を描き、
 * 選択中は SelectedMarker (黄色三角) を重ねる. クリック / タップで `onActivate` を呼ぶ.
 *
 * 起動は LegacyIconButton / ZoomHalf と同じく useTapActivate で pointer ベース化する
 * (ペン / Windows Ink の click 不発対策). cell は .map 内に並ぶため、hook を呼べる
 * 単位として 1 cell = 1 component に切り出している (親の map ループ内では hook を呼べない).
 * aria-label / aria-pressed / 1px 黒 border + 黒丸 dot の見た目は切り出し前と不変.
 */
const PenWidthCell = ({
  width,
  selected,
  onActivate,
}: {
  readonly width: number;
  readonly selected: boolean;
  readonly onActivate: () => void;
}): React.ReactNode => {
  const dot = getPenWidthCellDotDisplay(width);
  const tap = useTapActivate(onActivate);
  return (
    <button
      type="button"
      // aria-label は width 値ベース (現状の e2e/snapshot test との互換維持).
      // 値が変わると label も変わる点はユーザカスタマイズの自然な表現.
      aria-label={`ペン幅 ${width}px`}
      aria-pressed={selected}
      {...tap}
      data-manual="draw.size"
      // relative は SelectedMarker absolute 配置のアンカー。
      // Phase 6.7-B-fix-2B-b: 静的 1px 黒 border を復活 (色被り判別のため; ペン幅 cell
      // でも黒丸 dot との境界が見えるよう常時付与)。
      // focus ring は旧 Java applet UI 完全再現要求により抑制。
      className="relative flex h-6 items-center justify-center border border-black bg-white focus:outline-none focus-visible:outline-none"
    >
      <span
        aria-hidden
        // 20px が最大 (cell 24px の 80%、旧版 `PaintToolCanvas` cell 20px / ● 16px = 80%
        // のノスタルジー比率を踏襲). 1..4 はやや読みにくいので min 2px の floor. `w > 20`
        // のときは ● を 20px で頭打ちにし内側に数値併記する (consult 2026-05-09). pure
        // helper `getPenWidthCellDotDisplay` で算出.
        className="relative block rounded-full bg-black"
        style={{
          width: `${dot.dotPx}px`,
          height: `${dot.dotPx}px`,
        }}
      >
        {dot.showLabel ? (
          <span
            aria-hidden
            // ● (20px) 内側に数値を中央配置. 9px / 白 / bold で 2 桁 ("40", "60") が収まる
            // 最小サイズ + 黒 ● に対するコントラスト確保. SelectedMarker (= 黄色三角) は外側
            // button 直下の絶対配置なのでこの内側 label と z-index がぶつからない (= 三角は
            // 四隅、数値は中央).
            className="absolute inset-0 flex items-center justify-center text-[9px] font-bold leading-none text-white"
          >
            {dot.label}
          </span>
        ) : null}
      </span>
      {selected ? <SelectedMarker /> : null}
    </button>
  );
};

/**
 * 旧版 GIF icon ボタン. `_a1` (idle, default = 白背景) と `_a2`
 * (hover/active = オレンジ背景) の 2 枚ペアを group-hover で切り替える.
 * CSS のみで挙動再現 (JS state 不要).
 *
 * disabled の場合は idle のみ表示 + grayed out + cursor-not-allowed +
 * tooltip「今後のアップデートで対応予定」を `title` で出す.
 *
 * Note: ファイル名末尾の `_a1` / `_a2` の意味付けはユーザ確認 (2026-05-05)
 * により「a1 = 通常、a2 = ホバー」で確定。`_a1` の白背景がデフォルト、
 * ホバーで `_a2` のオレンジ背景に切り替わる。
 */
const LegacyIconButton = ({
  label,
  icon,
  onClick,
  disabled = false,
  pressed = false,
  tooltip,
  manualId,
}: {
  readonly label: string;
  /** `public/legacy-icons/{icon}_a1.gif` / `_a2.gif` の prefix. */
  readonly icon: string;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly pressed?: boolean;
  readonly tooltip?: string;
  /** 使い方マニュアル (user-manual-2026-09-19.md §10.2) の `data-manual` id。 */
  readonly manualId?: string;
}): React.ReactNode => {
  const idleSrc = `/legacy-icons/${icon}_a1.gif`;
  const activeSrc = `/legacy-icons/${icon}_a2.gif`;
  // 起動を pointer ベースへ (ペン/Windows Ink の click 不発対策、useTapActivate)。
  // disabled 時 / onClick 未指定時は no-op。onClick (キーボード用) は tap 側が温存し、
  // pointer 起動直後の二重 click はデデュープする。
  const tap = useTapActivate(disabled ? undefined : onClick);
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      {...tap}
      data-manual={manualId}
      title={tooltip}
      // group + group-hover は Tailwind 標準パターン. _a1/_a2 を hover で差替.
      // pressed (= 選択中) のときは active 画像を常時表示.
      // Phase 6.7-B-fix-2B-b: 機能ボタンも focus ring 抑制 (cell button / ペン種
      // ボタンと揃える, 旧 Java applet UI 完全再現要求)。
      // Phase 6.7 R15: ペン種ボタン (普通/透過) と class 体系を `legacyToggleButtonClass`
      // で完全統一. 形状 (`flex h-[26px] ... p-0.5`) と group は icon の hover 切替のため残す.
      // 高さは 2026-06-13 に h-7 (28px) → h-[26px] へ刈り込み (classic-sidebar-compaction.md §7).
      className={`group flex h-[26px] items-center justify-center p-0.5 ${legacyToggleButtonClass(pressed, disabled)}`}
    >
      {pressed || disabled ? (
        <img
          src={pressed ? activeSrc : idleSrc}
          alt=""
          className="block"
          style={{ imageRendering: 'pixelated' }}
        />
      ) : (
        <>
          <img
            src={idleSrc}
            alt=""
            className="block group-hover:hidden"
            style={{ imageRendering: 'pixelated' }}
          />
          <img
            src={activeSrc}
            alt=""
            className="hidden group-hover:block"
            style={{ imageRendering: 'pixelated' }}
          />
        </>
      )}
    </button>
  );
};

/**
 * 拡大 (+) / 縮小 (-) を **1 つの枠の半分ずつ** に並べる. 旧版踏襲
 * (PaintToolCanvas.java:554-585、`fillRect(3, ..., 44/2, ...)` で枠を半分割し
 * loupe_plus / loupe_minus を左右に置く設計).
 *
 * GIF アイコンが元から 20x20px (= 半幅サイズ) で、外枠 1 つ + 半幅ボタン × 2 の
 * 構成で旧版と同じ見た目になる. hover hi-light は半分側のみに乗る (= 旧版の
 * `mouseOverIcon == 7` / `== 8` ごとの fillRect と同じ挙動).
 *
 * 端到達時 (zoom === zoomMin で縮小不可 / zoom === zoomMax で拡大不可) は片側
 * のみ disabled. 反対側は引き続き押せる.
 */
const ZoomSplitButton = ({
  zoom,
  zoomMin,
  zoomMax,
  onZoomIn,
  onZoomOut,
}: {
  readonly zoom: number;
  readonly zoomMin: number;
  readonly zoomMax: number;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
}): React.ReactNode => {
  const inDisabled = zoom >= zoomMax;
  const outDisabled = zoom <= zoomMin;
  return (
    // 外枠は LegacyIconButton 等と同じ `h-[26px] + outsetFrame + bg-white` (28→26px の
    // 刈り込みは classic-sidebar-compaction.md §7). 内部 2 半分は
    // border 無しで hover 時にだけオレンジ薄背景 (= 旧版 hover 色 `(255, 212, 142)`
    // ≒ `--color-accent-soft`) を出す.
    // div は純粋に視覚的枠 (= 旧版の 1 枠半分割), role/aria-label は内部 button
    // 個別に持たせる方が a11y 的に意味的 (= biome a11y rule とも整合).
    <div className={`flex h-[26px] bg-white ${'border border-[var(--color-border)]'}`}>
      <ZoomHalf
        icon="loupe_plus"
        label="拡大"
        tooltip={inDisabled ? `拡大限界 (${zoomMax}x)` : `拡大 (現在 ${zoom}x)`}
        disabled={inDisabled}
        onClick={onZoomIn}
      />
      <ZoomHalf
        icon="loupe_minus"
        label="縮小"
        tooltip={outDisabled ? '等倍表示 (1x)' : `縮小 (現在 ${zoom}x)`}
        disabled={outDisabled}
        onClick={onZoomOut}
      />
    </div>
  );
};

const ZoomHalf = ({
  icon,
  label,
  tooltip,
  disabled,
  onClick,
}: {
  readonly icon: string;
  readonly label: string;
  readonly tooltip: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
}): React.ReactNode => {
  const idleSrc = `/legacy-icons/${icon}_a1.gif`;
  const activeSrc = `/legacy-icons/${icon}_a2.gif`;
  // 起動を pointer ベースへ (ペン / Windows Ink の click 不発対策、useTapActivate)。
  // disabled 側 (端到達: 拡大/縮小限界) は no-op。onClick (キーボード用) は tap が温存。
  const tap = useTapActivate(disabled ? undefined : onClick);
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      {...tap}
      data-manual="view.zoom"
      title={tooltip}
      className={`group flex flex-1 items-center justify-center p-0.5 focus:outline-none focus-visible:outline-none ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-[var(--color-accent-soft)] cursor-pointer'
      }`}
    >
      {disabled ? (
        <img src={idleSrc} alt="" className="block" style={{ imageRendering: 'pixelated' }} />
      ) : (
        <>
          <img
            src={idleSrc}
            alt=""
            className="block group-hover:hidden"
            style={{ imageRendering: 'pixelated' }}
          />
          <img
            src={activeSrc}
            alt=""
            className="hidden group-hover:block"
            style={{ imageRendering: 'pixelated' }}
          />
        </>
      )}
    </button>
  );
};
